import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, Download, ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Music } from "lucide-react";
import api, { API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";
import { toast } from "sonner";

function fmt(d) {
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }); }
  catch (e) { return d; }
}

export default function RunSheet() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const canManage = ["super_admin", "worship_director", "worship_leader"].includes(user?.role);
  useEffect(() => { api.get(`/services/${id}`).then((r) => setS(r.data)); }, [id]);
  if (!s) return <div className="wf-app-bg min-h-screen flex items-center justify-center"><LogoMark size={48} /></div>;

  const pdfUrl = `${API}/services/${id}/runsheet.pdf?auth=${encodeURIComponent(localStorage.getItem("wf_token") || "")}`;

  const saveSongs = async (songs) => {
    setS({ ...s, songs });
    try { const { data } = await api.put(`/services/${id}`, { songs }); setS(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addSong = () => saveSongs([...(s.songs || []), { id: crypto.randomUUID(), song_id: null, title: "New song", key: "", bpm: "", time_signature: "", notes: "" }]);
  const updateSong = (sid, fields) => setS({ ...s, songs: s.songs.map((x) => (x.id === sid ? { ...x, ...fields } : x)) });
  const commitSongs = () => saveSongs(s.songs);
  const removeSong = (sid) => saveSongs(s.songs.filter((x) => x.id !== sid));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= s.songs.length) return;
    const songs = [...s.songs]; const [m] = songs.splice(i, 1); songs.splice(j, 0, m); saveSongs(songs);
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0E1A]">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } } @page { margin: 16mm; }`}</style>
      <div className="no-print sticky top-0 bg-[#0A0E1A] text-white px-4 py-3 flex items-center justify-between z-10">
        <button onClick={() => nav(`/services/${id}`)} className="flex items-center gap-1.5 text-sm text-[#B8C1D1]"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="flex gap-2">
          <button onClick={() => window.print()} data-testid="print-runsheet" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg wf-gold-btn font-semibold text-sm"><Printer className="w-4 h-4" /> Print / Save PDF</button>
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="download-runsheet-pdf" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white font-semibold text-sm"><Download className="w-4 h-4" /> Download PDF</a>
        </div>
      </div>

      {canManage && (
        <div className="no-print bg-[#0E1524] border-b border-white/10 text-[#F7F5F0]">
          <div className="max-w-3xl mx-auto p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2 text-[#E5C378]"><Music className="w-4 h-4" /> Setlist — add songs</h2>
              <button onClick={addSong} data-testid="add-runsheet-song" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg wf-gold-btn text-sm font-semibold"><Plus className="w-4 h-4" /> Add song</button>
            </div>
            <p className="text-xs text-[#8A97AC] mb-3">Type any song title, key and BPM — it doesn't need to be in the library. Changes save automatically.</p>
            {(!s.songs || s.songs.length === 0) ? <p className="text-sm text-[#6E7D97]">No songs yet. Add your first one above.</p> : (
              <div className="space-y-2">
                {s.songs.map((song, i) => (
                  <div key={song.id} data-testid={`runsheet-song-${i}`} className="bg-[#0B101D] border border-white/10 rounded-xl p-2.5 flex items-center gap-2 flex-wrap">
                    <div className="flex flex-col">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-[#6E7D97] hover:text-[#E5C378] disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                      <button onClick={() => move(i, 1)} disabled={i === s.songs.length - 1} className="text-[#6E7D97] hover:text-[#E5C378] disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                    </div>
                    <span className="text-xs font-mono text-[#6E7D97] w-5">{i + 1}</span>
                    <input value={song.title || ""} onChange={(e) => updateSong(song.id, { title: e.target.value })} onBlur={commitSongs} placeholder="Song title"
                      data-testid={`runsheet-song-title-${i}`} className="flex-1 min-w-[140px] bg-transparent border-b border-white/10 text-sm text-[#F7F5F0] focus:outline-none focus:border-[#E5C378] py-1" />
                    <input value={song.key || ""} onChange={(e) => updateSong(song.id, { key: e.target.value })} onBlur={commitSongs} placeholder="Key"
                      className="w-16 bg-transparent border-b border-white/10 text-sm text-[#F7F5F0] text-center focus:outline-none focus:border-[#E5C378] py-1" />
                    <input value={song.bpm || ""} onChange={(e) => updateSong(song.id, { bpm: e.target.value })} onBlur={commitSongs} placeholder="BPM"
                      className="w-16 bg-transparent border-b border-white/10 text-sm text-[#F7F5F0] text-center focus:outline-none focus:border-[#E5C378] py-1" />
                    <button onClick={() => removeSong(song.id)} data-testid={`remove-runsheet-song-${i}`} className="text-[#6E7D97] hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto p-8">
        <div className="border-b-2 border-[#B88E38] pb-4 mb-6">
          <h1 className="text-3xl font-bold" style={{ fontFamily: "Space Grotesk, sans-serif" }}>{s.title}</h1>
          <div className="text-[#B88E38] font-medium mt-1">{fmt(s.date)} · {s.time} · {s.type}</div>
        </div>

        <Section title="Team">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#0A0E1A] text-white"><Th>Position</Th><Th>Name</Th><Th>Status</Th></tr></thead>
            <tbody>{s.team.map((a, i) => <tr key={a.id} className={i % 2 ? "bg-[#F5F1E9]" : ""}><Td>{a.slot}</Td><Td>{a.name}</Td><Td className="capitalize">{a.status}</Td></tr>)}</tbody>
          </table>
        </Section>

        <Section title="Setlist">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#0A0E1A] text-white"><Th>#</Th><Th>Song</Th><Th>Key</Th><Th>BPM</Th><Th>Time</Th><Th>Notes</Th></tr></thead>
            <tbody>{s.songs.map((song, i) => <tr key={song.id} className={i % 2 ? "bg-[#F5F1E9]" : ""}><Td>{i + 1}</Td><Td>{song.title}</Td><Td>{song.key}</Td><Td>{song.bpm}</Td><Td>{song.time_signature}</Td><Td>{song.notes}</Td></tr>)}</tbody>
          </table>
        </Section>

        <Section title="Service Flow">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#0A0E1A] text-white"><Th>#</Th><Th>Element</Th><Th>Detail</Th><Th>Note</Th></tr></thead>
            <tbody>{s.flow.map((f, i) => <tr key={f.id} className={i % 2 ? "bg-[#F5F1E9]" : ""}><Td>{i + 1}</Td><Td className="font-semibold">{f.type}</Td><Td>{f.title}</Td><Td className="italic text-gray-600">{f.note}</Td></tr>)}</tbody>
          </table>
        </Section>

        <p className="text-xs text-gray-400 italic mt-8">"At the right time, I, the LORD, will make it happen." — Isaiah 60:22</p>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mb-6"><h2 className="text-lg font-bold mb-2" style={{ fontFamily: "Space Grotesk, sans-serif" }}>{title}</h2>{children}</div>
);
const Th = ({ children }) => <th className="text-left px-3 py-2 font-semibold border border-[#D8D2C6]">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 border border-[#D8D2C6] ${className}`}>{children}</td>;
